import React, { useState } from 'react';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import styles from './EmissionFilters.module.css';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface FlightFilters {
  id?: string;
  status?: string;
  paymentStatus?: string;
  passengerName?: string;
  routeOutbound?: string;
  routeReturn?: string;
  minValue?: string;
  maxValue?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface BusFilters {
  pedidoId?: string;
  status?: string;
  paymentStatus?: string;
  passengerName?: string;
  routeOutbound?: string;
  routeReturn?: string;
  minValue?: string;
  maxValue?: string;
  dateFrom?: string;
  dateTo?: string;
}

type Filters = FlightFilters | BusFilters;

// ─── Labels de exibição ───────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  'emission started': 'Emissão Iniciada',
  'awaiting payment': 'Aguardando Pagamento',
  completed: 'Completado',
  cancelled: 'Cancelado',
  failed: 'Falhou',
  processando: 'Processando',
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  aprovado: 'Pag. Aprovado',
  processando: 'Pag. Processando',
  reprovado: 'Pag. Reprovado',
  reembolsado: 'Pag. Reembolsado',
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props<T extends Filters> {
  /** "flight" mostra campo id (UUID); "bus" mostra campo pedidoId */
  mode: 'flight' | 'bus';
  filters: T;
  onChange: (filters: T) => void;
  onApply: () => void;
}

// ─── Componente ───────────────────────────────────────────────────────────────

export function EmissionFilters<T extends Filters>({
  mode,
  filters,
  onChange,
  onApply,
}: Props<T>) {
  const [open, setOpen] = useState(false);

  const isBus = mode === 'bus';
  const busFilters = filters as BusFilters;
  const flightFilters = filters as FlightFilters;

  // Quantos filtros estão ativos (exceto page/limit)
  const activeEntries = Object.entries(filters).filter(([, v]) => v !== '' && v !== undefined);

  const set = (key: keyof T, value: string) => {
    onChange({ ...filters, [key]: value || undefined } as T);
  };

  const clear = (key: keyof T) => {
    const next = { ...filters };
    delete next[key];
    onChange(next as T);
  };

  const reset = () => {
    onChange({} as T);
    onApply();
  };

  const handleApply = () => {
    onApply();
    setOpen(false);
  };

  // Monta os chips de filtros ativos
  const chips: { key: keyof T; label: string }[] = activeEntries.map(([k, v]) => {
    const key = k as keyof T;
    let label = String(v);

    if (k === 'status') label = STATUS_LABELS[v] ?? v;
    else if (k === 'paymentStatus') label = PAYMENT_STATUS_LABELS[v] ?? v;
    else if (k === 'passengerName') label = `Pax: ${v}`;
    else if (k === 'routeOutbound') label = `Ida: ${v}`;
    else if (k === 'routeReturn') label = `Volta: ${v}`;
    else if (k === 'minValue') label = `≥ R$ ${v}`;
    else if (k === 'maxValue') label = `≤ R$ ${v}`;
    else if (k === 'dateFrom') label = `De: ${new Date(v).toLocaleDateString('pt-BR')}`;
    else if (k === 'dateTo') label = `Até: ${new Date(v).toLocaleDateString('pt-BR')}`;
    else if (k === 'id') label = `ID: ${v.substring(0, 8)}…`;
    else if (k === 'pedidoId') label = `Pedido: ${v}`;

    return { key, label };
  });

  return (
    <div className={styles.panel}>
      {/* Barra superior */}
      <div className={styles.topBar}>
        <button
          type="button"
          className={`${styles.toggleBtn} ${open ? styles.active : ''}`}
          onClick={() => setOpen((o) => !o)}
        >
          <Filter size={15} />
          Filtros
          {activeEntries.length > 0 && (
            <span style={{
              marginLeft: 4,
              background: open ? 'rgba(255,255,255,0.3)' : 'var(--color-blue)',
              color: '#fff',
              borderRadius: '10px',
              padding: '1px 7px',
              fontSize: 'var(--font-size-12)',
              fontWeight: 700,
            }}>
              {activeEntries.length}
            </span>
          )}
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {/* Chips de filtros ativos */}
        {chips.length > 0 && (
          <div className={styles.activeTags}>
            {chips.map(({ key, label }) => (
              <span key={String(key)} className={styles.tag}>
                {label}
                <button
                  type="button"
                  className={styles.tagRemove}
                  onClick={() => { clear(key); onApply(); }}
                  title="Remover filtro"
                >
                  <X size={11} />
                </button>
              </span>
            ))}
            <button type="button" className={styles.clearAll} onClick={reset}>
              Limpar tudo
            </button>
          </div>
        )}
      </div>

      {/* Painel expansível */}
      <div className={`${styles.expandedPanel} ${open ? styles.open : styles.closed}`}>
        <div className={styles.grid}>

          {/* ID do pedido */}
          {isBus ? (
            <div className={styles.field}>
              <label className={styles.label}>Pedido ID</label>
              <input
                className={styles.input}
                type="text"
                placeholder="PED-123456 ou UUID"
                value={busFilters.pedidoId ?? ''}
                onChange={(e) => set('pedidoId' as keyof T, e.target.value)}
              />
            </div>
          ) : (
            <div className={styles.field}>
              <label className={styles.label}>ID do Checkout (UUID)</label>
              <input
                className={styles.input}
                type="text"
                placeholder="e6d59fef-d36b…"
                value={flightFilters.id ?? ''}
                onChange={(e) => set('id' as keyof T, e.target.value)}
              />
            </div>
          )}

          {/* Status do pedido */}
          <div className={styles.field}>
            <label className={styles.label}>Status do Pedido</label>
            <select
              className={styles.select}
              value={filters.status ?? ''}
              onChange={(e) => set('status' as keyof T, e.target.value)}
            >
              <option value="">Todos</option>
              <option value="emission started">Emissão Iniciada</option>
              <option value="awaiting payment">Aguardando Pagamento</option>
              <option value="completed">Completado</option>
              <option value="cancelled">Cancelado</option>
              <option value="failed">Falhou</option>
            </select>
          </div>

          {/* Status do pagamento */}
          <div className={styles.field}>
            <label className={styles.label}>Status do Pagamento</label>
            <select
              className={styles.select}
              value={filters.paymentStatus ?? ''}
              onChange={(e) => set('paymentStatus' as keyof T, e.target.value)}
            >
              <option value="">Todos</option>
              <option value="aprovado">Aprovado</option>
              <option value="processando">Processando</option>
              <option value="reprovado">Reprovado</option>
              <option value="reembolsado">Reembolsado</option>
            </select>
          </div>

          {/* Nome do passageiro */}
          <div className={styles.field}>
            <label className={styles.label}>Nome do Passageiro</label>
            <input
              className={styles.input}
              type="text"
              placeholder={isBus ? 'Ex: Maria' : 'Ex: João'}
              value={filters.passengerName ?? ''}
              onChange={(e) => set('passengerName' as keyof T, e.target.value)}
            />
          </div>

          {/* Rota de ida */}
          <div className={styles.field}>
            <label className={styles.label}>Rota de Ida</label>
            <input
              className={styles.input}
              type="text"
              placeholder={isBus ? 'Ex: belo-horizonte' : 'Ex: GRU, São Paulo'}
              value={filters.routeOutbound ?? ''}
              onChange={(e) => set('routeOutbound' as keyof T, e.target.value)}
            />
          </div>

          {/* Rota de volta */}
          <div className={styles.field}>
            <label className={styles.label}>Rota de Volta</label>
            <input
              className={styles.input}
              type="text"
              placeholder={isBus ? 'Ex: sao-paulo' : 'Ex: GIG, Rio de Janeiro'}
              value={filters.routeReturn ?? ''}
              onChange={(e) => set('routeReturn' as keyof T, e.target.value)}
            />
          </div>

          {/* Faixa de valor */}
          <div className={styles.field}>
            <label className={styles.label}>Valor (R$)</label>
            <div className={styles.rangeRow}>
              <input
                className={styles.input}
                type="number"
                min={0}
                placeholder="Mín"
                value={filters.minValue ?? ''}
                onChange={(e) => set('minValue' as keyof T, e.target.value)}
              />
              <input
                className={styles.input}
                type="number"
                min={0}
                placeholder="Máx"
                value={filters.maxValue ?? ''}
                onChange={(e) => set('maxValue' as keyof T, e.target.value)}
              />
            </div>
          </div>

          {/* Período de criação */}
          <div className={styles.field}>
            <label className={styles.label}>Período de Criação</label>
            <div className={styles.rangeRow}>
              <input
                className={styles.input}
                type="date"
                value={filters.dateFrom ?? ''}
                onChange={(e) => set('dateFrom' as keyof T, e.target.value)}
              />
              <input
                className={styles.input}
                type="date"
                value={filters.dateTo ?? ''}
                onChange={(e) => set('dateTo' as keyof T, e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.resetBtn} onClick={reset}>
            Limpar
          </button>
          <button type="button" className={styles.applyBtn} onClick={handleApply}>
            Aplicar Filtros
          </button>
        </div>
      </div>
    </div>
  );
}
